/**
 * Toolbar pegado arriba con acciones de edición.
 * - Modo vista: botón "Editar página" + acciones secundarias
 * - Modo edición: botones Guardar / Cancelar + toggles + Eliminar
 */
import type { ReactNode } from 'react';
import { Pencil, Save, X, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';

interface Props {
  editing: boolean;
  onEdit: () => void;
  onSave: () => void;
  onCancel: () => void;
  saving?: boolean;
  /** Acciones extra cuando NO está en edición (ej. nuevo artículo, eliminar). */
  viewActions?: ReactNode;
  /** Acciones extra cuando ESTÁ en edición (toggles, eliminar). */
  editActions?: ReactNode;
}

export function EditToolbar({
  editing,
  onEdit,
  onSave,
  onCancel,
  saving,
  viewActions,
  editActions,
}: Props) {
  return (
    <div
      className={cn(
        'bg-background/95 sticky top-0 z-30 -mx-8 mb-6 flex flex-wrap items-center gap-2 border-b px-8 py-2.5 backdrop-blur',
        editing && 'border-brand/40 bg-brand/5',
      )}
    >
      {editing ? (
        <>
          <Button
            variant="brand"
            size="sm"
            onClick={onSave}
            disabled={saving}
            className="font-semibold"
          >
            {saving ? (
              <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
            ) : (
              <Save className="mr-1.5 h-3.5 w-3.5" />
            )}
            Guardar cambios
          </Button>
          <Button variant="ghost" size="sm" onClick={onCancel} disabled={saving}>
            <X className="mr-1.5 h-3.5 w-3.5" />
            Cancelar
          </Button>
          {editActions && (
            <>
              <Separator orientation="vertical" className="mx-1 h-6" />
              {editActions}
            </>
          )}
          <span className="text-brand ml-auto text-xs font-medium">● Modo edición</span>
        </>
      ) : (
        <>
          <Button variant="default" size="sm" onClick={onEdit}>
            <Pencil className="mr-1.5 h-3.5 w-3.5" />
            Editar página
          </Button>
          {viewActions && (
            <>
              <Separator orientation="vertical" className="mx-1 h-6" />
              {viewActions}
            </>
          )}
        </>
      )}
    </div>
  );
}

/**
 * Toggle inline tipo chip para flags booleanos (destacado, publicado).
 */
export function ToggleChip({
  pressed,
  onPressedChange,
  icon: Icon,
  children,
}: {
  pressed: boolean;
  onPressedChange: (v: boolean) => void;
  icon?: typeof Pencil;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={() => onPressedChange(!pressed)}
      className={cn(
        'inline-flex h-8 items-center gap-1.5 rounded-md border px-2.5 text-xs font-medium transition-colors',
        pressed
          ? 'bg-brand/15 text-brand border-brand/30'
          : 'border-input text-muted-foreground hover:bg-accent',
      )}
    >
      {Icon && <Icon className="h-3.5 w-3.5" />}
      {children}
      <span
        className={cn(
          'ml-1 inline-block h-4 w-7 rounded-full transition-colors',
          pressed ? 'bg-brand' : 'bg-muted',
        )}
      >
        <span
          className={cn(
            'block h-3.5 w-3.5 rounded-full bg-white shadow transition-transform',
            pressed ? 'translate-x-3 translate-y-[1px]' : 'translate-x-[1px] translate-y-[1px]',
          )}
        />
      </span>
    </button>
  );
}
